import { modelProfileOperation } from '@agentic/contracts';
import { Badge, Button, formatNumber, KeyValue, Section, SkeletonRows, useToast } from '@agentic/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { config, PRODUCT_NAME } from '../../app/config.ts';
import { DisclosureSummary } from '../../app/disclosure.tsx';
import { modelsQuery } from '../../app/queries.ts';
import { isOperator } from '../../app/roles.ts';
import { QueryError } from '../../app/route-states.tsx';
import { PAGE, PageHeader } from '../../app/shell.tsx';
import { type ThemePreference, useTheme } from '../../app/theme.ts';
import { useMe } from '../../app/use-me.ts';
import { DEFAULT_CHANGE_COPY } from '../../features/model-management/format.ts';
import { useManagementStatus, useSetDefault } from '../../features/model-management/hooks.ts';
import { MutationError } from '../../features/model-management/states.tsx';

export const Route = createFileRoute('/_app/settings/')({ component: SettingsPage });

function SettingsPage() {
  const me = useMe();
  return (
    <>
      <PageHeader title="Settings" />
      <div className={`${PAGE} items-start xl:grid-cols-2`}>
        <section aria-labelledby="prefs-h" className="flex flex-col gap-4">
          <div>
            <h2 id="prefs-h" className="text-[20px] leading-snug font-semibold tracking-[-0.01em]">
              Preferences
            </h2>
            <p className="wb-prose text-ink-2">
              Your own appearance and account. Changes here affect only you.
            </p>
          </div>
          <ThemeSetting />
          <Account />
        </section>
        <section aria-labelledby="platform-h" className="mt-4 flex flex-col gap-4 xl:mt-0">
          <div>
            <h2 id="platform-h" className="text-[20px] leading-snug font-semibold tracking-[-0.01em]">
              Platform settings
            </h2>
            <p className="wb-prose text-ink-2">
              These apply to everyone in tenant <code className="font-mono text-[13px]">{me.tenantId}</code>,
              not only to you. {isOperator(me) ? '' : 'Only operators can change them.'}
            </p>
          </div>
          <ModelSetting />
        </section>
      </div>
    </>
  );
}

function ModelSetting() {
  const me = useMe();
  const toast = useToast();
  const q = useQuery(modelsQuery);
  const status = useManagementStatus();
  const { mutation: save, managed } = useSetDefault(status.data);
  const [choice, setChoice] = useState<string | null>(null);
  if (q.isPending || status.isPending) return <SkeletonRows rows={4} label="Loading model profiles" />;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  const canChange = managed ? !!status.data?.caller.canManage : isOperator(me);
  const selected = choice ?? q.data.default;
  const effective = new Map((q.data.effective ?? []).map((e) => [e.name, e]));
  // Default candidates: chat profiles only (an embeddings profile is never a reasoning default), and with the
  // effective projection, only active, enabled profiles.
  const reasoning = q.data.items.filter((m) => {
    const e = effective.get(m.name);
    if (e) return e.operation === 'chat' && e.enabled && e.activeVersion !== null;
    return modelProfileOperation(m) === 'chat';
  });
  return (
    <Section title="Default reasoning model profile" eyebrow="Model routing" headingLevel={3}>
      <p className="wb-prose mb-3 text-ink-2" data-testid="default-copy">
        The model profile agents that follow the platform default use for reasoning. {DEFAULT_CHANGE_COPY}{' '}
        Data policy and region are part of each profile.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(
            { profile: selected },
            {
              onSuccess: (res) => {
                setChoice(null);
                toast.push({
                  title: `Default reasoning profile: ${res.default}`,
                  description: 'Newly started episode attempts use it; rerun a proposal to compare.',
                  tone: 'ok',
                });
              },
            },
          );
        }}
        className="flex flex-col gap-3"
      >
        <fieldset disabled={!canChange} className="flex flex-col gap-2">
          <legend className="sr-only">Model profile</legend>
          {reasoning.map((m) => {
            const e = effective.get(m.name);
            return (
              <label
                key={m.name}
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-line p-4 transition-colors duration-150 hover:bg-surface-2 has-[:checked]:border-primary has-[:checked]:bg-surface-2"
              >
                <input
                  type="radio"
                  name="profile"
                  value={m.name}
                  checked={selected === m.name}
                  onChange={() => setChoice(m.name)}
                  className="mt-1 accent-[var(--wb-accent)]"
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <code className="font-mono text-[13px] font-medium">{m.name}</code>
                    {m.name === q.data.default && <Badge tone="info">current</Badge>}
                    {e?.activeVersion && (
                      <span className="font-mono text-[12px] text-ink-3">v{e.activeVersion}</span>
                    )}
                  </span>
                  <span className="block font-mono text-[12.5px] text-ink-2">
                    {e ? `${e.connectionId} · ${e.modelId}` : `${m.adapter} · ${m.model}`}
                  </span>
                  <span className="block text-[13px] text-ink-3">
                    {m.region} · {m.latencyClass} · {m.dataPolicy}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>
        {!canChange && (
          <p className="text-[13px] text-ink-3">
            {managed
              ? 'Only operators of the model-management tenant can change the default profile.'
              : 'Only operators can change the default profile.'}
          </p>
        )}
        {canChange && (
          <Button
            type="submit"
            variant="primary"
            className="self-start"
            disabled={selected === q.data.default}
            loading={save.isPending}
          >
            Use {selected}
          </Button>
        )}
        {save.isError && (
          <MutationError
            error={save.error}
            title="The default model was not changed"
            onReload={() => {
              save.reset();
              void q.refetch();
            }}
          />
        )}
      </form>
      <nav aria-label="Model management" className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13.5px]">
        <Link
          to="/settings/models"
          className="font-medium underline underline-offset-2"
          data-testid="manage-models-link"
        >
          Model profiles and versions
        </Link>
        <Link
          to="/settings/models"
          search={{ tab: 'catalog' }}
          className="font-medium underline underline-offset-2"
          data-testid="manage-catalog-link"
        >
          Available models
        </Link>
        <Link
          to="/settings/models"
          search={{ tab: 'connections' }}
          className="font-medium underline underline-offset-2"
          data-testid="manage-connections-link"
        >
          Connections
        </Link>
      </nav>
      <details className="group mt-4">
        <DisclosureSummary>All model profiles ({q.data.items.length})</DisclosureSummary>
        <ul className="mt-2 flex flex-col gap-3">
          {q.data.items.map((m) => {
            const e = effective.get(m.name);
            return (
              <li key={m.name} className="rounded-xl border border-line p-4">
                <code className="font-mono text-[13px] font-medium">{m.name}</code>
                <KeyValue
                  className="mt-2"
                  items={[
                    {
                      label: 'Adapter',
                      value: <code className="font-mono text-[12.5px]">{e?.adapter ?? m.adapter}</code>,
                    },
                    {
                      label: 'Model',
                      value: <code className="font-mono text-[12.5px]">{e?.modelId ?? m.model}</code>,
                    },
                    ...(e
                      ? [
                          {
                            label: 'Active version',
                            value: e.activeVersion ? `v${e.activeVersion}` : 'none',
                          },
                          {
                            label: 'Connection',
                            value: <code className="font-mono text-[12.5px]">{e.connectionId}</code>,
                          },
                          { label: 'Model approval', value: e.approvalState },
                        ]
                      : []),
                    { label: 'Intended tasks', value: m.intendedTasks.join(', ') || 'Any' },
                    { label: 'Region', value: m.region },
                    { label: 'Data policy', value: m.dataPolicy },
                    { label: 'Latency class', value: m.latencyClass },
                    { label: 'Fallback eligible', value: m.fallbackEligible ? 'Yes' : 'No' },
                    {
                      label: 'Fallback profiles',
                      value: (e?.fallbackNames ?? m.fallbackProfiles).join(', ') || 'None',
                    },
                    {
                      label: 'Consequential',
                      value: m.consequential ? 'Yes (never falls back silently)' : 'No',
                    },
                    { label: 'Max context tokens', value: formatNumber(m.maxContextTokens) },
                    ...(m.embeddingDimensions
                      ? [{ label: 'Embedding dimensions', value: m.embeddingDimensions }]
                      : []),
                  ]}
                />
              </li>
            );
          })}
        </ul>
      </details>
    </Section>
  );
}

const THEME_LABELS: Record<ThemePreference, string> = {
  system: 'Match system',
  light: 'Light',
  dark: 'Dark',
};

function ThemeSetting() {
  const [theme, setTheme] = useTheme();
  const options: ThemePreference[] = ['system', 'light', 'dark'];
  return (
    <Section title="Theme" headingLevel={3}>
      <fieldset className="inline-flex gap-1 rounded-full border border-line bg-surface-2 p-1">
        <legend className="sr-only">Theme</legend>
        {options.map((o) => (
          <label
            key={o}
            className="inline-flex min-h-[36px] cursor-pointer items-center rounded-full px-4 text-[14px] font-medium text-ink-2 transition-colors duration-150 hover:text-ink has-[:checked]:bg-primary has-[:checked]:text-on-primary has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus"
          >
            <input
              type="radio"
              name="theme"
              value={o}
              checked={theme === o}
              onChange={() => setTheme(o)}
              className="sr-only"
            />
            {THEME_LABELS[o]}
          </label>
        ))}
      </fieldset>
    </Section>
  );
}

function Account() {
  const me = useMe();
  return (
    <Section title="Account" headingLevel={3}>
      <KeyValue
        items={[
          { label: 'Name', value: me.displayName },
          { label: 'Roles', value: me.roles.join(', ') || '—' },
          {
            label: 'Access groups',
            value: me.aclSubjects.length ? (
              <code className="font-mono text-[12.5px]">{me.aclSubjects.join(', ')}</code>
            ) : (
              'None'
            ),
          },
        ]}
      />
      <details className="group mt-3">
        <DisclosureSummary>Technical details</DisclosureSummary>
        <KeyValue
          className="mt-2"
          items={[
            { label: 'Principal', value: <code className="font-mono text-[12.5px]">{me.id}</code> },
            { label: 'Tenant', value: <code className="font-mono text-[12.5px]">{me.tenantId}</code> },
            { label: 'ACL subjects', value: me.aclSubjects.join(', ') || '—' },
            { label: 'Product', value: PRODUCT_NAME },
            { label: 'API mode', value: <code className="font-mono text-[12.5px]">{config.apiMode}</code> },
          ]}
        />
      </details>
    </Section>
  );
}
